// The operator console's half of the platform sign-in texts (owner's decision B1, 4.11.2026) — one read
// and one write, mirroring `PlatformSignInTextsEndpoints` and the anonymous route beside it.
//
// THE READ IS THE ANONYMOUS ONE, and that is deliberate rather than a shortcut. There is no platform-gated
// twin of `GET /api/auth/sign-in-texts`, because the console and the sign-in page are asking the same
// question about the same four strings, and the strings are shown to unauthenticated visitors anyway — an
// authenticated copy would protect nothing and would be a second answer to one question (the endpoint's
// own doc comment says so, and gives `TenantSettingsEndpoints`' logo route as the precedent). So the form
// fills itself from exactly what the page it configures will read.
//
// THE WRITE ANSWERS WHAT IT PUT IN FORCE, so the form needs no second read after saving — which is also
// what makes the saved state and the rendered state provably the same thing.

import type { components } from "@clms/contracts";
import { apiFetch } from "@web/lib/api";
import type { SignInTextField, SignInTexts } from "./signInTexts";

/** The texts in force (PlatformSignInTextsDto). A null is "nothing set — the shipped default is in force". */
export type PlatformSignInTextsDto = components["schemas"]["PlatformSignInTextsDto"];

/** A REPLACE of all four: a field left out, null or blank is cleared (SavePlatformSignInTextsRequest). */
export type SavePlatformSignInTextsRequest =
  components["schemas"]["SavePlatformSignInTextsRequest"];

/** Normalise the contract's `null | string` into the console's own `string | null`. */
function toSignInTexts(dto: PlatformSignInTextsDto): SignInTexts {
  return {
    taglineFi: dto.taglineFi ?? null,
    taglineEn: dto.taglineEn ?? null,
    taglineSubFi: dto.taglineSubFi ?? null,
    taglineSubEn: dto.taglineSubEn ?? null,
  };
}

/** The texts in force, read the way the sign-in page reads them. */
export async function fetchSignInTexts(signal?: AbortSignal): Promise<SignInTexts> {
  return toSignInTexts(await apiFetch<PlatformSignInTextsDto>("/api/auth/sign-in-texts", { signal }));
}

/**
 * Replace all four texts (PLATFORM_ADMIN + the console's own client). Answers what is now in force, which
 * is what the form adopts — so "restore the defaults" and a save are the same call and the same ending.
 */
export async function saveSignInTexts(
  body: Record<SignInTextField, string | null>,
  signal?: AbortSignal,
): Promise<SignInTexts> {
  const saved = await apiFetch<PlatformSignInTextsDto>("/api/platform/sign-in-texts", {
    method: "PUT",
    body: body satisfies SavePlatformSignInTextsRequest,
    signal,
  });
  return toSignInTexts(saved);
}
